import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import api, { LOGO_URL } from "@/lib/api";
import {
  ArrowLeft, Save, Eye, EyeOff, Trash2, Image as ImageIcon, Bold, Italic, List, ListOrdered,
  Link as LinkIcon, Heading2, Heading3, Quote, Upload, FolderOpen
} from "lucide-react";
import ImageField from "@/components/admin/ImageField";
import MediaPicker from "@/components/admin/MediaPicker";

const CATEGORIES = [
  "Wedding Planning","Wedding Venues","Destination Weddings","Cultural Weddings",
  "Photography Tips","Videography Tips","Real Weddings","Behind the Scenes"
];

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/[\s-]+/g, "-");
}

export default function BlogEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === "new";
  const [post, setPost] = useState({
    title: "", slug: "", category: "Wedding Planning", excerpt: "", content: "",
    cover_image: "", author: "Mujtaba Emal · Elite Pixels", published: true,
    seo_title: "", seo_description: "",
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [insertingImage, setInsertingImage] = useState(false);
  const textareaRef = useRef(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("ep_token")) { nav("/admin/login"); return; }
    if (isNew) return;
    (async () => {
      try {
        const r = await api.get("/admin/blog");
        const p = r.data.find(x => x.id === id);
        if (!p) { toast.error("Post not found"); nav("/admin"); return; }
        setPost({ ...p, seo_title: p.seo_title || "", seo_description: p.seo_description || "" });
      } catch (e) {
        if (e.response?.status === 401) { localStorage.removeItem("ep_token"); nav("/admin/login"); }
      } finally { setLoading(false); }
    })();
  }, [id, isNew, nav]);

  const update = (patch) => setPost(p => ({ ...p, ...patch }));

  // Auto-generate slug while typing title (only if user hasn't manually edited slug)
  const onTitleChange = (e) => {
    const title = e.target.value;
    setPost(p => ({ ...p, title, slug: !p._slugTouched ? slugify(title) : p.slug }));
  };

  const onSlugChange = (e) => setPost(p => ({ ...p, slug: slugify(e.target.value), _slugTouched: true }));

  // Insert text at cursor in the textarea
  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const before = post.content.slice(0, start);
    const sel = post.content.slice(start, end);
    const after = post.content.slice(end);
    const replaced = text.replace("{sel}", sel);
    const next = before + replaced + after;
    update({ content: next });
    // Restore focus and place caret after inserted text
    setTimeout(() => {
      ta.focus();
      const pos = start + replaced.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const toolbarActions = [
    { icon: Heading2, title: "Heading 2", action: () => insertAtCursor("\n## {sel}\n") },
    { icon: Heading3, title: "Heading 3", action: () => insertAtCursor("\n### {sel}\n") },
    { icon: Bold, title: "Bold", action: () => insertAtCursor("**{sel}**") },
    { icon: Italic, title: "Italic", action: () => insertAtCursor("*{sel}*") },
    { icon: List, title: "Bullet List", action: () => insertAtCursor("\n- {sel}\n") },
    { icon: ListOrdered, title: "Numbered List", action: () => insertAtCursor("\n1. {sel}\n") },
    { icon: Quote, title: "Quote", action: () => insertAtCursor("\n> {sel}\n") },
    { icon: LinkIcon, title: "Link", action: () => {
        const url = window.prompt("Link URL (e.g. /blog/some-post or https://...)", "https://");
        if (!url) return;
        insertAtCursor(`[{sel}](${url})`);
      } },
  ];

  const onInsertImageFromLibrary = (m) => {
    const url = m.urls?.large || m.url;
    insertAtCursor(`\n\n![${m.alt || m.title || ""}](${url})\n\n`);
  };

  const uploadAndInsertContent = async (file) => {
    if (!file?.type?.startsWith("image/")) { toast.error("Image files only"); return; }
    toast.info("Uploading...");
    try {
      const form = new FormData(); form.append("file", file);
      const r = await api.post("/admin/upload", form);
      const url = r.data.urls?.large || r.data.url;
      insertAtCursor(`\n\n![${r.data.title || ""}](${url})\n\n`);
      toast.success("Image inserted");
    } catch { toast.error("Upload failed"); }
  };

  // Drag-and-drop into the content textarea
  const onTextareaDrop = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("border-[#D4AF37]");
    const file = Array.from(e.dataTransfer?.files || []).find(f => f.type.startsWith("image/"));
    if (file) uploadAndInsertContent(file);
  }, []); // eslint-disable-line

  // Paste image directly from clipboard
  const onTextareaPaste = useCallback((e) => {
    const item = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith("image/"));
    if (item) {
      e.preventDefault();
      uploadAndInsertContent(item.getAsFile());
    }
  }, []); // eslint-disable-line

  const save = async () => {
    if (!post.title.trim()) { toast.error("Title is required"); return; }
    if (!post.content.trim()) { toast.error("Content is required"); return; }
    setSaving(true);
    try {
      const payload = { ...post };
      delete payload._slugTouched;
      if (!payload.slug) payload.slug = slugify(payload.title);
      if (!payload.excerpt) payload.excerpt = payload.content.slice(0, 200) + "...";
      if (isNew) {
        const r = await api.post("/admin/blog", payload);
        toast.success("Post created");
        nav(`/admin/blog/${r.data.id}`);
      } else {
        await api.put(`/admin/blog/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post permanently?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Deleted");
    nav("/admin");
  };

  if (loading) return <div className="min-h-screen bg-[#070707] flex items-center justify-center text-white/40">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#070707] flex" data-testid="blog-editor">
      <aside className="w-64 border-r border-white/10 p-6 sticky top-0 h-screen flex flex-col">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="" className="h-12 object-contain" />
          <div>
            <div className="font-serif text-base leading-tight">Elite Pixels</div>
            <div className="text-[0.55rem] tracking-[0.25em] uppercase text-white/40">Blog Editor</div>
          </div>
        </div>
        <Link to="/admin" className="text-xs text-white/60 hover:text-[#D4AF37] flex items-center gap-2 mb-6" data-testid="back-to-admin">
          <ArrowLeft size={14}/> Back to Dashboard
        </Link>
        <div className="space-y-3 mt-2">
          <button onClick={save} disabled={saving} className="btn-gold w-full justify-center" data-testid="save-post-btn">
            <Save size={14}/> {saving ? "Saving..." : isNew ? "Publish" : "Save"}
          </button>
          {!isNew && (
            <>
              <button onClick={() => update({ published: !post.published })} className="btn-ghost w-full justify-center">
                {post.published ? <><EyeOff size={14}/> Unpublish</> : <><Eye size={14}/> Publish</>}
              </button>
              {post.slug && post.published && (
                <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" className="btn-ghost w-full justify-center" data-testid="view-public-post">
                  <Eye size={14}/> View Public
                </a>
              )}
              <button onClick={remove} className="text-xs text-white/40 hover:text-red-500 flex items-center gap-2 mt-4">
                <Trash2 size={12}/> Delete post
              </button>
            </>
          )}
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-x-hidden max-w-[1400px] mx-auto">
        {/* Title + Slug */}
        <div className="mb-6">
          <input value={post.title} onChange={onTitleChange} placeholder="A Quiet Wedding at Casa Loma..."
            data-testid="post-title"
            className="w-full bg-transparent border-0 border-b border-white/10 px-0 py-3 font-serif text-4xl focus:border-[#D4AF37] outline-none placeholder:text-white/20" />
          <div className="mt-2 text-xs flex items-center gap-2 text-white/40">
            <span>/blog/</span>
            <input value={post.slug} onChange={onSlugChange} data-testid="post-slug"
              className="bg-transparent border-b border-white/10 flex-1 max-w-md py-1 focus:border-[#D4AF37] outline-none text-[#D4AF37]" />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left — content */}
          <div className="lg:col-span-2 space-y-5">
            {/* Featured image */}
            <ImageField label="Featured Image (Cover)" value={post.cover_image}
              onChange={(url) => update({ cover_image: url })} aspect="aspect-video"
              variant="large" testId="cover-image" />

            {/* Excerpt */}
            <label className="block">
              <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Excerpt (1–2 sentences shown on listings)</span>
              <textarea value={post.excerpt} onChange={e => update({ excerpt: e.target.value })} rows={2}
                data-testid="post-excerpt"
                placeholder="A short, story-driven summary that draws readers in..."
                className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
            </label>

            {/* Toolbar + body */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Content (Markdown)</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => setPreview(p => !p)}
                    className={`text-[0.6rem] tracking-widest uppercase px-3 py-1.5 border ${preview ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/15 text-white/50"}`}
                    data-testid="toggle-preview">
                    {preview ? "Editing" : "Preview"}
                  </button>
                </div>
              </div>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-1 mb-2 p-2 bg-[#0a0a0a] border border-white/10 border-b-0">
                {toolbarActions.map((a, i) => (
                  <button key={i} type="button" onClick={a.action} title={a.title}
                    className="p-2 text-white/60 hover:text-[#D4AF37] hover:bg-white/5">
                    <a.icon size={14}/>
                  </button>
                ))}
                <div className="w-px h-5 bg-white/10 mx-1" />
                <button type="button" onClick={() => setInsertingImage(true)}
                  className="px-3 py-1.5 text-xs tracking-widest uppercase text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center gap-1.5"
                  data-testid="insert-image-btn">
                  <ImageIcon size={12}/> Insert Image
                </button>
                <span className="text-[0.6rem] text-white/30 ml-2">Or drag &amp; drop / paste an image</span>
              </div>

              {preview ? (
                <div className="border border-white/10 bg-[#0a0a0a] px-5 py-4 min-h-[500px]" data-testid="content-preview">
                  <div className="prose prose-invert prose-lg max-w-none
                                  prose-headings:font-serif prose-headings:font-light
                                  prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                                  prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                                  prose-p:text-white/75 prose-p:leading-relaxed
                                  prose-strong:text-white prose-a:text-[#D4AF37]
                                  prose-img:my-4">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content || "*Nothing to preview yet — write some content!*"}</ReactMarkdown>
                  </div>
                </div>
              ) : (
                <textarea ref={textareaRef} value={post.content} onChange={e => update({ content: e.target.value })}
                  data-testid="post-content"
                  onDrop={onTextareaDrop} onPaste={onTextareaPaste}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[#D4AF37]"); }}
                  onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-[#D4AF37]"); }}
                  placeholder="Write your post in Markdown. Use the toolbar above, or drag an image right into this box."
                  rows={22}
                  className="w-full bg-black border border-white/15 px-4 py-3 text-sm leading-relaxed font-mono focus:border-[#D4AF37] outline-none transition-colors" />
              )}
              <div className="text-[0.6rem] text-white/30 mt-2">
                Tip: type <code className="text-[#D4AF37]">**bold**</code>, <code className="text-[#D4AF37]">## Heading</code>, <code className="text-[#D4AF37]">[link](/about)</code>. Images stored in your Media Library.
              </div>
            </div>
          </div>

          {/* Right — settings */}
          <aside className="space-y-5">
            <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-4">
              <h4 className="font-serif text-lg">Settings</h4>
              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Category</span>
                <select value={post.category} onChange={e => update({ category: e.target.value })}
                  data-testid="post-category"
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Author</span>
                <input value={post.author} onChange={e => update({ author: e.target.value })}
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!post.published} onChange={e => update({ published: e.target.checked })}
                  data-testid="post-published" className="accent-[#D4AF37]" />
                <span>Published</span>
              </label>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-4">
              <h4 className="font-serif text-lg">SEO</h4>
              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">SEO Title</span>
                <input value={post.seo_title} onChange={e => update({ seo_title: e.target.value })}
                  placeholder={post.title}
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                <span className="text-[0.55rem] text-white/30">{(post.seo_title || post.title).length}/60</span>
              </label>
              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Meta Description</span>
                <textarea value={post.seo_description} onChange={e => update({ seo_description: e.target.value })}
                  rows={3} placeholder={post.excerpt}
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                <span className="text-[0.55rem] text-white/30">{(post.seo_description || post.excerpt).length}/160</span>
              </label>
            </div>
          </aside>
        </div>
      </main>

      {insertingImage && (
        <MediaPicker
          onClose={() => setInsertingImage(false)}
          onSelect={(m) => { onInsertImageFromLibrary(m); setInsertingImage(false); }}
        />
      )}
    </div>
  );
}
